// チャットへのドラッグ＆ドロップで「何を送り、何を除外したか」を決める共通ロジック。
//
// ドロップは「これを送りたい」という明確な意思表示なので、送れるものは送り切る。
// 混在（画像＋ファイル）を拒否したり、超過分を黙って捨てたりしない。
// 除外が発生したときだけ、何がなぜ除外されたかを具体的に伝える。
//
// 送信そのものと送信後の副作用（サイドバー更新・再取得・返信下書きの解除など）は
// 画面側が持つ。ここは判定と文言だけを持つ。

export const CHAT_DROP_MAX_IMAGES = 5;
export const CHAT_DROP_MAX_BYTES = 100 * 1024 * 1024;

import { isChatImageFile } from './chatImageCompatibility';

// JPEG/PNG/WebP/AVIF はBackendで互換形式へ正規化し、HEIC/HEIFは送信前にbrowserでJPEG化する。
// GIF等のアニメーション形式は静止画へ黙って変えず、画像メッセージの対象外にする。
export const isChatDropImage = (file) => isChatImageFile(file);

/**
 * @param {FileList|File[]} input ドロップされたファイル
 * @param {{maxImages?: number, maxBytes?: number, rejectedCount?: number}} options
 *   rejectedCount は useChatDragDrop がサイズ上限で先に除外した件数。
 * @returns {{images: File[], files: File[], notices: string[], notice: string, hasSendable: boolean}}
 *   images = 1メッセージとしてまとめて送る画像、files = 1件ずつ順に送るその他のファイル。
 */
export function buildChatDropPlan(input, options = {}) {
  const {
    maxImages = CHAT_DROP_MAX_IMAGES,
    maxBytes = CHAT_DROP_MAX_BYTES,
    rejectedCount = 0,
  } = options;

  const incoming = Array.from(input || []).filter(Boolean);
  const withinSize = incoming.filter((file) => typeof file?.size !== "number" || file.size <= maxBytes);
  const oversizedCount = incoming.length - withinSize.length + Math.max(0, Number(rejectedCount) || 0);

  const allImages = withinSize.filter(isChatDropImage);
  const images = allImages.slice(0, maxImages);
  const imageOverflowCount = allImages.length - images.length;
  const files = withinSize.filter((file) => !isChatDropImage(file));

  const notices = [];
  if (oversizedCount > 0) {
    notices.push(`${Math.round(maxBytes / (1024 * 1024))}MBを超えるファイル${oversizedCount}件は送信できません。`);
  }
  if (imageOverflowCount > 0) {
    notices.push(`画像は一度に${maxImages}枚までのため、${imageOverflowCount}枚を除外しました。`);
  }

  return {
    images,
    files,
    notices,
    notice: notices.join(" "),
    hasSendable: images.length > 0 || files.length > 0,
  };
}

// 送信結果の要約文言。画像とファイルが混在しても1回で伝える。
export function describeChatDropResult({ imageCount = 0, fileCount = 0 } = {}) {
  const parts = [];
  if (imageCount > 0) parts.push(`画像${imageCount}枚`);
  if (fileCount > 0) parts.push(`ファイル${fileCount}件`);
  if (!parts.length) return "";
  return `${parts.join("と")}を送信しました`;
}
